import React from 'react';
import { useTranslation } from 'react-i18next';
import { isOptionalTest } from '../utils/optionalTestUtils';
import '../styles/TestSuiteSwimlanes.css';

interface TestTicket {
  id: number;
  ticketNumber: string;
  status: 'todo' | 'inProgress' | 'passed' | 'failed';
  description?: string;
  date: string;
  vatRate: string;
  requiresUpload?: boolean;
  assignee?: {
    name: string;
    avatar: string;
  };
  testNumber?: number;
  receipt?: any;
  testSuiteRuleMappingId?: number;
  testName?: string;
}

interface TestSuiteSwimlanesProps {
  tickets: TestTicket[];
  onTicketClick: (ticket: TestTicket) => void;
  onTicketDrop?: (ticketId: number, newStatus: 'todo' | 'inProgress' | 'passed' | 'failed') => void;
  showCounts?: boolean;
  isAdmin?: boolean; // Controls admin-only features like drag & drop and create test suite button
  onMarkAsOptional?: (ticket: TestTicket) => void; // New prop for handling optional test marking
}

const TestSuiteSwimlanes: React.FC<TestSuiteSwimlanesProps> = ({ 
  tickets, 
  onTicketClick,
  onTicketDrop,
  showCounts = true,
  isAdmin = true,
  onMarkAsOptional
}) => {
  const { t } = useTranslation();

  // Filtered tickets for each swimlane
  const todoTickets = tickets.filter(ticket => ticket.status === 'todo');
  const inProgressTickets = tickets.filter(ticket => ticket.status === 'inProgress');
  const passedTickets = tickets.filter(ticket => ticket.status === 'passed');
  const failedTickets = tickets.filter(ticket => ticket.status === 'failed');

  // Function to handle drag start
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, ticketId: number) => {
    if (!onTicketDrop || !isAdmin) return;
    e.dataTransfer.setData('text/plain', ticketId.toString());
    e.currentTarget.classList.add('dragging');
  };

  // Function to handle drag end
  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('dragging');
  };

  // Function to handle drag over
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!onTicketDrop || !isAdmin) return;
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  // Function to handle drag leave
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove('drag-over');
  };

  // Function to handle drop
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, newStatus: 'todo' | 'inProgress' | 'passed' | 'failed') => {
    if (!onTicketDrop || !isAdmin) return;
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    const ticketId = parseInt(e.dataTransfer.getData('text/plain'), 10);
    onTicketDrop(ticketId, newStatus);
  };

  // Ticket component
  const TicketComponent: React.FC<{ ticket: TestTicket }> = ({ ticket }) => {
    const isOptional = isOptionalTest(ticket.testName || ticket.description || '');
    
    return (
      <div 
        className="test-ticket" 
        draggable={!!onTicketDrop && isAdmin}
        onDragStart={onTicketDrop && isAdmin ? (e) => handleDragStart(e, ticket.id) : undefined}
        onDragEnd={onTicketDrop && isAdmin ? handleDragEnd : undefined}
        onClick={() => onTicketClick(ticket)}
      >
        <div className="ticket-header">
          <div className="ticket-number">{ticket.testNumber || ticket.id}</div>
          <div className="ticket-date">{ticket.date}</div>
        </div>
        <div className="ticket-content">
          <div className="ticket-title">{ticket.testName || ticket.description}</div>
        </div>
        
        <div className="ticket-footer">
          {/* Mark as optional link - positioned at the start of the footer */}
          {isOptional && onMarkAsOptional && (
            <div className="optional-link-container">
              <button 
                className="optional-link"
                onClick={(e) => {
                  e.stopPropagation(); // Prevent ticket click
                  onMarkAsOptional(ticket);
                }}
              >
                {t('testSuite.markAsOptional', 'Mark as optional')}
              </button>
            </div>
          )}

          {/* Mobile status change — shown for admins as drag-and-drop doesn't work on touch */}
          {isAdmin && onTicketDrop && (
            <select
              className="ticket-status-select"
              value={ticket.status}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                onTicketDrop(ticket.id, e.target.value as 'todo' | 'inProgress' | 'passed' | 'failed');
              }}
            >
              <option value="todo">{t('testSuite.toDo', 'To Do')}</option>
              <option value="inProgress">{t('testSuite.inProgress', 'In Progress')}</option>
              <option value="failed">{t('testSuite.failed', 'Failed')}</option>
              <option value="passed">{t('testSuite.passed', 'Passed')}</option>
            </select>
          )}

          <div className="ticket-actions">
            {/* Show tick icon when receipt is uploaded */}
            {ticket.receipt && (
              <div className="receipt-status-indicator" title={t('testSuite.receiptUploaded') || 'Test Uploaded'}>
                <span className="tick-icon"></span>
                <span className="receipt-uploaded-text">{t('testSuite.receiptUploaded') || 'Test Uploaded'}</span>
              </div>
            )}
            
            {/* Show upload button only when receipt is null */}
            {!ticket.receipt && (
              <button 
                className="upload-button" 
                title={t('testSuite.uploadReceipt') || 'Upload Test'}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent ticket click
                  onTicketClick(ticket); // Still open the modal for upload
                }}
              >
                <span className="upload-icon"></span>
              </button>
            )}
            
            {/* Show download button only when receipt exists */}
            {ticket.receipt && (
              <button 
                className="icon-button" 
                title={t('testSuite.downloadReceipt') || 'Download Test'}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent ticket click
                  onTicketClick(ticket); // Open the modal to show download option
                }}
              >
                <span className="download-icon"></span>
              </button>
            )}
            
            {/* Show assignee avatar if exists */}
            {(ticket.status === 'inProgress' || ticket.status === 'passed' || ticket.status === 'failed') && ticket.assignee && (
              <div className="user-avatar" title={ticket.assignee.name}>
                <img src={ticket.assignee.avatar} alt={ticket.assignee.name} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="swimlane-container">
      {/* Update the column order in the swimlane header */}
      <div className="swimlane-header">
        <div className="swimlane-column">
          <h2>{t('testSuite.toDo')}</h2>
          {showCounts && (
            <div className="count">
              <span>{todoTickets.length} {t('testSuite.outOf')} {tickets.length}</span>
            </div>
          )}
        </div>
        <div className="swimlane-column">
          <h2>{t('testSuite.inProgress')}</h2>
          {showCounts && (
            <div className="count">
              <span>{inProgressTickets.length} {t('testSuite.outOf')} {tickets.length}</span>
            </div>
          )}
        </div>
        <div className="swimlane-column">
          <h2>{t('testSuite.failed')}</h2>
          {showCounts && (
            <div className="count">
              <span>{failedTickets.length} {t('testSuite.outOf')} {tickets.length}</span>
            </div>
          )}
        </div>
        <div className="swimlane-column">
          <h2>{t('testSuite.passed')}</h2>
          {showCounts && (
            <div className="count">
              <span>{passedTickets.length} {t('testSuite.outOf')} {tickets.length}</span>
            </div>
          )}
        </div>
      </div>

      {/* Update the swimlane body with the same order */}
      <div className="swimlane-body">
        <div 
          className="swimlane-column"
          onDragOver={onTicketDrop && isAdmin ? handleDragOver : undefined}
          onDragLeave={onTicketDrop && isAdmin ? handleDragLeave : undefined}
          onDrop={onTicketDrop && isAdmin ? (e) => handleDrop(e, 'todo') : undefined}
        >
          {todoTickets.map(ticket => (
            <TicketComponent key={ticket.id} ticket={ticket} />
          ))}
        </div>
        
        <div 
          className="swimlane-column"
          onDragOver={onTicketDrop && isAdmin ? handleDragOver : undefined}
          onDragLeave={onTicketDrop && isAdmin ? handleDragLeave : undefined}
          onDrop={onTicketDrop && isAdmin ? (e) => handleDrop(e, 'inProgress') : undefined}
        >
          {inProgressTickets.map(ticket => (
            <TicketComponent key={ticket.id} ticket={ticket} />
          ))}
        </div>
        
        <div 
          className="swimlane-column"
          onDragOver={onTicketDrop && isAdmin ? handleDragOver : undefined}
          onDragLeave={onTicketDrop && isAdmin ? handleDragLeave : undefined}
          onDrop={onTicketDrop && isAdmin ? (e) => handleDrop(e, 'failed') : undefined}
        >
          {failedTickets.map(ticket => (
            <TicketComponent key={ticket.id} ticket={ticket} />
          ))}
        </div>
        
        <div 
          className="swimlane-column"
          onDragOver={onTicketDrop && isAdmin ? handleDragOver : undefined}
          onDragLeave={onTicketDrop && isAdmin ? handleDragLeave : undefined}
          onDrop={onTicketDrop && isAdmin ? (e) => handleDrop(e, 'passed') : undefined}
        >
          {passedTickets.map(ticket => (
            <TicketComponent key={ticket.id} ticket={ticket} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TestSuiteSwimlanes;